/**
 * Como a tela SENATRAN fala da integração (contrato v3, ADR-034).
 *
 * O envio é **recebido** na hora e o resultado chega depois, pelo retorno: registrado, ou
 * recusado com o motivo. A trilha ganhou os eventos do contrato — passagem, pagamento e
 * retorno — ao lado de envio e consulta, e cada um precisa de um nome que o operador leia.
 */

import type {
  DecisaoRegistrada,
  DetalheDoTransito,
  EventoDaIntegracao,
  NotificacaoDoTransito,
} from "../api/tipos";

export type Cor = "verde" | "amarelo" | "vermelho" | "azul" | "cinza";

const OPERACOES: Record<string, string> = {
  ENVIO: "Envio",
  CONSULTA: "Consulta",
  PASSAGEM: "Passagem",
  PAGAMENTO: "Pagamento",
  RETORNO: "Retorno",
};

/** O nome da linha da trilha. Operação desconhecida aparece como veio, e não some. */
export function rotuloDaOperacao(operacao: string): string {
  return OPERACOES[operacao] ?? operacao;
}

/** Falha e recusa com o mesmo peso: "não tentamos" e "tentamos e falhou" são fatos diferentes. */
export function corDaSituacao(situacao: string | null | undefined): Cor {
  if (!situacao) return "cinza";
  const s = situacao.toUpperCase();
  if (s === "FALHA" || s.startsWith("RECUSAD")) return "vermelho";
  if (s.startsWith("CANCELAD") || s === "VENCIDO") return "amarelo";
  return "verde";
}

/** O retorno do processamento, como aparece no cartão de envio. */
export function apresentacaoDoRetorno(
  retorno: string | null | undefined,
): { texto: string; cor: Cor } | null {
  switch (retorno) {
    case "AGUARDANDO":
      return { texto: "aguardando retorno", cor: "azul" };
    case "REGISTRADA":
      return { texto: "registrada", cor: "verde" };
    case "RECUSADA":
      return { texto: "recusada", cor: "vermelho" };
    default:
      return null;
  }
}

/** Os desfechos que a concessionária informa, na ordem em que a tela os oferece. */
export const DESFECHOS_INFORMAVEIS = [
  "PENDENTE_PAGAMENTO",
  "PAGO",
  "ISENTO",
  "CANCELADO",
] as const;

export type DesfechoInformavel = (typeof DESFECHOS_INFORMAVEIS)[number];

/**
 * O que o supervisor pode informar agora: o que o domínio permite (`destinos_possiveis`,
 * calculado no servidor) e o contrato aceita como desfecho. Capturado ainda não está
 * registrado na SENATRAN, e não tem desfecho a informar.
 */
export function desfechosPossiveis(
  status: string,
  destinos: readonly string[],
): DesfechoInformavel[] {
  if (status === "CAPTURADO") return [];
  return DESFECHOS_INFORMAVEIS.filter((d) => destinos.includes(d));
}

// ------------------------------------------------------------ o histórico (tarefa 10)

type TransicaoRegistrada = DetalheDoTransito["historico"][number];

/** Uma linha do histórico: decisão da operação, troca com a SENATRAN, mudança de status ou o
 *  aviso que ela gerou (`R-08`, `R-09`). */
export type EntradaDoHistorico =
  | { tipo: "decisao"; em: string; decisao: DecisaoRegistrada }
  | { tipo: "integracao"; em: string; evento: EventoDaIntegracao }
  | { tipo: "status"; em: string; transicao: TransicaoRegistrada }
  | { tipo: "notificacao"; em: string; notificacao: NotificacaoDoTransito };

/**
 * No mesmo instante, a troca com a SENATRAN vem antes da mudança de status: o retorno que
 * registra a passagem e a ida a "Em processamento" são gravados na mesma transação, e a
 * causa se lê antes do efeito. O aviso que a transição gerou vem depois dela.
 */
const ORDEM_NO_EMPATE = {
  decisao: 0,
  integracao: 1,
  status: 2,
  notificacao: 3,
} as const;

const EVENTOS_DA_NOTIFICACAO: Record<string, string> = {
  DEBITO_PUBLICADO: "Débito publicado",
  RECLASSIFICACAO_TAG: "Reclassificação da TAG",
  CANCELAMENTO_AUTOMATICO_7D: "Cancelamento depois de 7 dias",
  CANCELAMENTO_MANUAL: "Cancelamento informado",
  AVISO_CNH_8D: "1º aviso ao condutor (8º dia)",
  AVISO_CNH_15D: "2º aviso ao condutor (15º dia)",
};

const DESTINATARIOS: Record<string, string> = {
  SUGESTAO_AUTUACAO: "Sugestão de Autuação",
  OSA: "operadora da TAG (OSA)",
  BACKOFFICE: "backoffice",
  MONITORIA_CDT: "monitoria CDT",
  USUARIO_CNH: "condutor, pela CNH Digital",
};

/** "1º aviso ao condutor (8º dia) → condutor, pela CNH Digital". Desconhecido vem como veio. */
export function descricaoDaNotificacao(n: NotificacaoDoTransito): string {
  const evento = EVENTOS_DA_NOTIFICACAO[n.evento] ?? n.evento;
  return `${evento} → ${DESTINATARIOS[n.destinatario] ?? n.destinatario}`;
}

/**
 * O instante do registro, quando o do fato é outro (`O-04`): na demonstração com o relógio
 * avançado, a transição aconteceu "dia 17" no tempo dela e foi registrada hoje; na carga
 * histórica, o fato é de antes da carga. Com menos de um minuto de diferença, nada a dizer.
 */
export function instanteRealSeDiferente(
  em: string,
  real: string | null | undefined,
): string | null {
  if (!real) return null;
  return Math.abs(Date.parse(em) - Date.parse(real)) >= 60_000 ? real : null;
}

/**
 * O histórico inteiro, em ordem (tarefa 10). Até 25/09 o botão mostrava as decisões e as
 * mudanças de status, e as tentativas de envio viviam num cartão à parte — a recusa de
 * uma tentativa e o reenvio que passou não apareciam na sequência em que aconteceram.
 *
 * Só ordena o que já veio da API: nenhuma regra é decidida aqui.
 */
export function historicoEmOrdem(
  detalhe: Pick<DetalheDoTransito, "decisoes" | "historico"> &
    Partial<Pick<DetalheDoTransito, "notificacoes">>,
  trilha: EventoDaIntegracao[],
): EntradaDoHistorico[] {
  const entradas: EntradaDoHistorico[] = [
    ...detalhe.decisoes.map((decisao) => ({
      tipo: "decisao" as const,
      em: decisao.em,
      decisao,
    })),
    ...trilha.map((evento) => ({
      tipo: "integracao" as const,
      em: evento.em,
      evento,
    })),
    ...detalhe.historico.map((transicao) => ({
      tipo: "status" as const,
      em: transicao.em,
      transicao,
    })),
    ...(detalhe.notificacoes ?? []).map((notificacao) => ({
      tipo: "notificacao" as const,
      em: notificacao.em ?? notificacao.criado_em,
      notificacao,
    })),
  ];
  return entradas
    .map((entrada, posicao) => ({ entrada, posicao }))
    .sort(
      (a, b) =>
        Date.parse(a.entrada.em) - Date.parse(b.entrada.em) ||
        ORDEM_NO_EMPATE[a.entrada.tipo] - ORDEM_NO_EMPATE[b.entrada.tipo] ||
        a.posicao - b.posicao,
    )
    .map(({ entrada }) => entrada);
}

/** "Passagem · tentativa 2", "Retorno nº 1": o número que a trilha grava, com o nome certo. */
export function tituloDaTroca(evento: EventoDaIntegracao): string {
  const envio = ["ENVIO", "PASSAGEM", "PAGAMENTO"].includes(evento.operacao);
  return `${rotuloDaOperacao(evento.operacao)} · ${envio ? "tentativa" : "nº"} ${evento.tentativa}`;
}
